import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  type DetailsProps,
  InfoPanel,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
  formatDateTime,
  getFormDiff,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, useFormikContext } from 'formik';
import {
  type ContainerRegistryConnection,
  ContainerRegistryConnectionsDocument,
  type ContainerRegistryConnectionsQuery,
  CreateContainerRegistryConnectionDocument,
  type CreateContainerRegistryConnectionMutation,
  type CreateContainerRegistryConnectionMutationVariables,
  DeleteContainerRegistryConnectionDocument,
  type DeleteContainerRegistryConnectionMutation,
  type DeleteContainerRegistryConnectionMutationVariables,
  UpdateContainerRegistryConnectionDocument,
  type UpdateContainerRegistryConnectionMutation,
  type UpdateContainerRegistryConnectionMutationVariables,
} from 'generated/hosting-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ensureError } from '../../../../common/errors/ensure-error';

const containerRegistryDetailsSchema = Yup.object<ObjectSchemaDefinition>({
  host: Yup.string().required('Hostname is required'),
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

export const ContainerRegistryDetails: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(false);
  const [containerRegistryConnectionData, setContainerRegistryConnectionData] =
    useState<ContainerRegistryConnection>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Query for existing values
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );
        const response =
          await envApolloClient?.query<ContainerRegistryConnectionsQuery>({
            query: ContainerRegistryConnectionsDocument,
            fetchPolicy: 'no-cache',
          });

        const registryConnectionNodes =
          response?.data?.containerRegistryConnections?.nodes;

        if (registryConnectionNodes && registryConnectionNodes.length > 0) {
          setContainerRegistryConnectionData(registryConnectionNodes[0]);
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const Panel: React.FC = () => {
    const { values } = useFormikContext<ContainerRegistryConnection>();
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Last Created">
            {containerRegistryConnectionData?.id &&
              `${formatDateTime(
                containerRegistryConnectionData?.createdDate,
              )} by ${containerRegistryConnectionData?.createdUser}`}
          </Paragraph>
          <Paragraph title="Last Modified">
            {containerRegistryConnectionData?.id &&
              `${formatDateTime(
                containerRegistryConnectionData?.updatedDate,
              )} by ${containerRegistryConnectionData?.updatedUser}`}
          </Paragraph>
        </Section>
        <Section>
          <Paragraph title="Tenant ID">{values?.tenantId ?? ''}</Paragraph>
          <Paragraph title="Environment ID">{envId}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const saveData = useCallback(
    async (
      formData: ContainerRegistryConnection,
      initialData: DetailsProps<ContainerRegistryConnection>['initialData'],
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );
      if (formData.id) {
        await envApolloClient?.mutate<
          UpdateContainerRegistryConnectionMutation,
          UpdateContainerRegistryConnectionMutationVariables
        >({
          mutation: UpdateContainerRegistryConnectionDocument,
          variables: {
            input: {
              patch: getFormDiff(formData, initialData?.data),
              id: formData?.id,
            },
          },
        });
      } else {
        await envApolloClient?.mutate<
          CreateContainerRegistryConnectionMutation,
          CreateContainerRegistryConnectionMutationVariables
        >({
          mutation: CreateContainerRegistryConnectionDocument,
          variables: {
            input: {
              containerRegistryConnection: getFormDiff(
                formData,
                initialData?.data,
              ) as ContainerRegistryConnection,
            },
          },
        });
      }
    },
    [envId],
  );

  return (
    <Details<ContainerRegistryConnection>
      defaultTitle="Container Registry"
      subtitle="Container Registry Details"
      stationMessage={
        containerRegistryConnectionData?.id
          ? {
              title:
                'The Hosting Service currently supports container registries that implement the Docker Registry HTTP API v2.',
              type: 'info',
            }
          : undefined
      }
      initialData={{
        data:
          containerRegistryConnectionData ??
          ({
            host: '',
            username: '',
            password: '',
          } as ContainerRegistryConnection),
        loading,
        error,
      }}
      saveData={saveData}
      validationSchema={containerRegistryDetailsSchema}
      actions={
        containerRegistryConnectionData?.id
          ? [
              {
                label: 'Delete',
                confirmationMode: 'Advanced',
                confirmationConfig: {
                  body: `Deleting the container registry connection is a destructive operation. Any customized services using this container registry will now result in errors during deployments.`,
                },
                onActionSelected: async () => {
                  const envApolloClient = environmentApolloClient(
                    envId,
                    new URL(
                      `/graphql`,
                      config.hostingServiceAdminBaseUrl,
                    ).toString(),
                  );
                  await envApolloClient?.mutate<
                    DeleteContainerRegistryConnectionMutation,
                    DeleteContainerRegistryConnectionMutationVariables
                  >({
                    mutation: DeleteContainerRegistryConnectionDocument,
                    variables: {
                      input: { id: containerRegistryConnectionData?.id ?? '' },
                    },
                  });
                  history.push(
                    `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config`,
                  );
                },
              },
            ]
          : []
      }
      infoPanel={containerRegistryConnectionData?.id ? <Panel /> : undefined}
    >
      <Field
        name="host"
        label="Registry Host"
        as={SingleLineTextField}
        tooltipContent={`The host of the container registry. You can find this in documentation of the corresponding container registry. For example for "Docker Hub", the registry host is "registry.hub.docker.com".`}
      />
      <Field
        name="username"
        label="Username"
        as={SingleLineTextField}
        tooltipContent={`The username used to connect to the container registry. This account needs to have permissions to "Read/Pull" container image tags for a given repository from the container registry.`}
      />
      <Field
        name="password"
        label="Password"
        type="password"
        as={SingleLineTextField}
        autoComplete="one-time-code"
        tooltipContent={`The password used to connect to the container registry.`}
      />
    </Details>
  );
};

export const ContainerRegistryDetailsCrumb: BreadcrumbResolver = () =>
  'Container Registry';
